// src/components/AMAPanel.js — Admin panel for Ask Me Anything
// Shows all member questions, allows Sky Bri to reply
// Reply is sent to member's Weekly Q tab with notification
import { useState, useEffect } from 'react'
import { supabase, sendMessage, SKY_BRI_USER_ID } from '../lib/supabase'
import './AMAPanel.css'

export default function AMAPanel({ toast }) {
  const [questions, setQuestions] = useState([])
  const [loading, setLoading]     = useState(true)
  const [replies, setReplies]     = useState({})
  const [filter, setFilter]       = useState('unanswered')
  const [search, setSearch]       = useState('')

  useEffect(() => {
    loadQuestions()
    // Real-time for new questions
    const sub = supabase.channel('admin-ama')
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'ama_questions'
      }, () => loadQuestions())
      .subscribe()
    return () => sub.unsubscribe()
  }, [])

  async function loadQuestions() {
    setLoading(true)
    const { data } = await supabase
      .from('ama_questions')
      .select('*, profiles(display_name, tier, id)')
      .order('created_at', { ascending: false })
    if (data) setQuestions(data)
    setLoading(false)
  }

  async function handleReply(questionId, memberId) {
    const answer = replies[questionId]?.trim()
    if (!answer) return

    // Save answer to ama_questions
    const { error } = await supabase
      .from('ama_questions')
      .update({
        answer,
        answered: true,
        answered_at: new Date().toISOString()
      })
      .eq('id', questionId)

    if (error) { toast.show('Failed to send reply', 'error'); return }

    // Also send as a DM notification so member knows to check Weekly Q tab
    await sendMessage({
      senderId: SKY_BRI_USER_ID,
      receiverId: memberId,
      content: `🌹 Sky Bri answered your question in the Ask Me Anything tab. Go check it out!`
    })

    toast.show('Reply sent! Member has been notified.', 'success')
    setReplies(prev => ({ ...prev, [questionId]: '' }))
    loadQuestions()
  }

  const tierColor = { gold: 'var(--gold)', premium: 'var(--plat)', platinum: 'var(--plat-bright)' }

  const filtered = questions
    .filter(q => {
      if (filter === 'unanswered') return !q.answered
      if (filter === 'answered')   return q.answered
      return true
    })
    .filter(q => {
      if (!search) return true
      const name = q.profiles?.display_name?.toLowerCase() || ''
      const text = q.question?.toLowerCase() || ''
      return name.includes(search.toLowerCase()) || text.includes(search.toLowerCase())
    })

  const unansweredCount = questions.filter(q => !q.answered).length

  return (
    <div className="ama-admin">
      {/* Stats */}
      <div className="ama-admin__stats">
        <div className="ama-admin__stat">
          <span style={{ color: 'var(--gold)' }}>{unansweredCount}</span>
          <span>Waiting</span>
        </div>
        <div className="ama-admin__stat">
          <span style={{ color: '#4CAF7D' }}>{questions.filter(q => q.answered).length}</span>
          <span>Answered</span>
        </div>
        <div className="ama-admin__stat">
          <span style={{ color: 'var(--cream)' }}>{questions.length}</span>
          <span>Total</span>
        </div>
      </div>

      {/* Filters */}
      <div className="ama-admin__filters">
        <div className="ac-filter">
          {['all', 'unanswered', 'answered'].map(f => (
            <button key={f}
              className={`ac-filter-btn ${filter === f ? 'ac-filter-btn--active' : ''}`}
              onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'unanswered' && unansweredCount > 0 && (
                <span style={{
                  marginLeft: 6, background: 'var(--gold)', color: '#080706',
                  borderRadius: 8, padding: '1px 6px', fontSize: 10, fontWeight: 700
                }}>{unansweredCount}</span>
              )}
            </button>
          ))}
        </div>
        <input
          className="admin-search"
          placeholder="Search questions or members..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
      </div>

      {/* Questions list */}
      {loading && <p style={{ padding: 20, color: 'var(--muted)', fontSize: 13 }}>Loading...</p>}
      {!loading && filtered.length === 0 && (
        <p style={{ padding: 24, color: 'var(--dim)', fontSize: 13, textAlign: 'center' }}>
          No questions found
        </p>
      )}

      <div className="ama-admin__list">
        {filtered.map(q => (
          <div key={q.id} className={`ama-admin__item ${q.answered ? 'ama-admin__item--done' : ''}`}>

            <div className="ama-admin__item-header">
              <div className="ama-admin__member">
                <div className="ama-admin__avatar">
                  {q.profiles?.display_name?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <span className="ama-admin__name">{q.profiles?.display_name || 'Member'}</span>
                  <span className="ama-admin__tier"
                    style={{ color: tierColor[q.profiles?.tier] }}>
                    {q.profiles?.tier}
                  </span>
                </div>
              </div>
              <div className="ama-admin__meta">
                <span className="ama-admin__date">
                  {new Date(q.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
                <span className={`ama-admin__status ${q.answered ? 'ama-admin__status--done' : 'ama-admin__status--pending'}`}>
                  {q.answered ? 'Answered' : 'Waiting'}
                </span>
              </div>
            </div>

            <p className="ama-admin__question">"{q.question}"</p>

            {q.answered ? (
              <div className="ama-admin__answer">
                <span className="ama-admin__answer-label">Your reply</span>
                <p className="ama-admin__answer-text">{q.answer}</p>
              </div>
            ) : (
              <div className="ama-admin__reply-form">
                <textarea
                  className="ama-admin__reply-input"
                  placeholder="Type your reply to this member... (they'll be notified)"
                  rows={2}
                  value={replies[q.id] || ''}
                  onChange={e => setReplies(prev => ({ ...prev, [q.id]: e.target.value }))}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && e.ctrlKey) handleReply(q.id, q.profiles?.id)
                  }}
                />
                <div className="ama-admin__reply-row">
                  <span style={{ fontSize: 11, color: 'var(--dim)' }}>Ctrl + Enter to send</span>
                  <button
                    className="btn btn--gold btn--sm"
                    onClick={() => handleReply(q.id, q.profiles?.id)}
                    disabled={!replies[q.id]?.trim()}>
                    Send Reply
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
